<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { useQueryClient } from '@tanstack/vue-query'
import { InfoIcon, Signature } from 'lucide-vue-next'
import DataStatePage from 'pejer/src/components/DataState/DataStatePage.vue'
import { Button } from 'pejer/src/components/ui/button'
import Separator from 'pejer/src/components/ui/separator/Separator.vue'
import { Table, TableBody, TableCell, TableRow } from 'pejer/src/components/ui/table'
import { toast } from 'pejer/src/components/ui/toast'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

definePage({
  meta: {
    unauthenticatedAccess: true,
    hideNavbar: true,
  },
})
const route = useRoute()
const pejer = usePejer()
if ('BankIntegrationBasketID' in route.params === false)
  throw new Error('BankIntegrationBasketID is required')

const consentID = route.params.BankIntegrationConsentID
const basketID = route.params.BankIntegrationBasketID

const { data: consent, dataState: consentDataState } = usePejerQuery(
  pejer.urls.bankIntegration.getConsent,
  {
    queryKey: ['bank-integration'],
    pathArgs: {
      BankIntegrationConsentID: route.params.BankIntegrationConsentID,
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const isSelectedSymbol = Symbol('isSelected')

const { data: basket, dataState: basketDataState } = usePejerQuery(
  pejer.urls.bankIntegration.getBasket,
  {
    queryKey: ['bank-integration', consentID, 'basket', basketID],
    pathArgs: {
      BankIntegrationConsentID: consentID,
      BankIntegrationBasketID: basketID,
    },
  },
  {
    ignoreAuthorization: true,
  }
)
const pendingPayments = computed(
  () =>
    basket.value?.data.bankIntegrationPayments.filter(
      (p) => p.statusCode === 'AUTHORIZATION_REQUIRED' || p.statusCode === 'IN_QUEUE'
    ) ?? []
)

const completedPayments = computed(
  () =>
    basket.value?.data.bankIntegrationPayments.filter(
      (p) => p.statusCode !== 'AUTHORIZATION_REQUIRED'
    ) ?? []
)

// const payments = ref<
//   (Parameters<
//     typeof pejer.urls.bankIntegration.getBasket.formatter
//   >[0]['data']['bankIntegrationPayments'][number] & {
//     [isSelectedSymbol]: boolean
//   })[]
// >([])

const paymentsMap = ref<
  Record<
    string,
    {
      payment: Parameters<
        typeof pejer.urls.bankIntegration.getBasket.formatter
      >[0]['data']['bankIntegrationPayments'][number]

      [isSelectedSymbol]: Date | null
    }
  >
>({})

watch(
  () => pendingPayments.value,
  (payments) => {
    const newPaymentsMap: Record<string, (typeof paymentsMap.value)[string]> = {}
    payments?.forEach((payment) => {
      newPaymentsMap[payment.bankIntegrationPaymentID] = {
        [isSelectedSymbol]: null,
        payment,
      }
    })
    // loop through old payments map and keep selected state if payment still exists
    Object.entries(paymentsMap.value).forEach(([paymentID, paymentInfo]) => {
      if (paymentID in newPaymentsMap) {
        const { payment, ...rest } = paymentInfo
        newPaymentsMap[paymentID] = {
          ...newPaymentsMap[paymentID],
          ...rest,
        }
      }
    })
    paymentsMap.value = newPaymentsMap
  },
  {
    immediate: true,
  }
)

const chosenPayments = computed(() => {
  return Object.values(paymentsMap.value)
    .filter((paymentInfo) => paymentInfo[isSelectedSymbol] !== null)
    .sort((a, b) => {
      if (a[isSelectedSymbol] === null || b[isSelectedSymbol] === null) return 0
      return a[isSelectedSymbol].getTime() - b[isSelectedSymbol].getTime()
    })
    .map((paymentInfo) => paymentInfo.payment)
})

const getPaymentBank = (payment: (typeof pendingPayments.value)[number]) => {
  // We connect the payment to an account via the creditorAccountBban, and then connect the account to a bank via the bic
  const account = consent.value?.accounts.find(
    (account) => account.bban === payment.debtorAccountBban
  )
  if (!account) return null
  return banks.value?.[account.bic] ?? null
}

const currentlyChosenBank = computed(() => {
  const chosenPayment = chosenPayments.value[0]
  if (!chosenPayment) return null
  return getPaymentBank(chosenPayment)
})

const { data: banks } = usePejerQuery(
  pejer.urls.bankIntegration.getBanks,
  {
    queryKey: ['banks'],
    pathArgs: {
      BankIntegrationConsentID: consentID,
    },
    select: (data) => {
      const banksMap: Record<string, (typeof data)[number]> = {}
      data.forEach((bank) => {
        banksMap[bank.bic] = bank
      })
      return banksMap
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const { mutate: signPaymentsMutation, dataState: signPaymentsDataState } = usePejerMutation(
  pejer.urls.bankIntegration.signPayments,
  {
    queryKeys: [['bank-integration']],
    onSuccess: (data) => {
      if (data.data.redirectUrl) window.location.href = data.data.redirectUrl

      if (data.data.status === 'REJECTED')
        toast({
          title: 'Betalinger avvist',
          description: 'Banken avviste betalingene, vennligst prøv igjen.',
          variant: 'destructive',
        })
    },
  },
  {
    ignoreAuthorization: true,
  }
)

// We want to group payments into sub baskets based on the bank they are associated with, and the basket size of that bank.
const paymentGroups = computed(() => {
  const groups: {
    bic: string
    payments: (typeof pendingPayments.value)[number][]
  }[] = []

  pendingPayments.value
    .filter((payment) => payment.statusCode === 'AUTHORIZATION_REQUIRED')
    .forEach((payment) => {
      const bank = getPaymentBank(payment)
      if (!bank) return

      let group = groups.find((g) => g.bic === bank.bic && g.payments.length < bank.basketSize)
      if (!group) {
        group = { bic: bank.bic, payments: [] }
        groups.push(group)
      }
      group.payments.push(payment)
    })
  return groups
})

const signPayments = async () => {
  signPaymentsMutation({
    pathArgs: {
      BankIntegrationConsentID: consentID,
      BankIntegrationBasketID: basketID,
    },
    body: {
      BankIntegrationPaymentIDs: chosenPayments.value.map(
        (payment) => payment.bankIntegrationPaymentID
      ),
    },
  })
}

const selectAllPayments = () => {
  let selectedBank = currentlyChosenBank.value
  Object.values(paymentsMap.value).forEach((paymentInfo) => {
    if (!selectedBank) selectedBank = getPaymentBank(paymentInfo.payment)

    if (!selectedBank)
      throw new Error('Could not determine bank for payment, cannot select all payments')

    if (chosenPayments.value.length >= selectedBank.basketSize) return

    if (getPaymentBank(paymentInfo.payment)?.bic !== selectedBank.bic) return
    paymentInfo[isSelectedSymbol] = new Date()
  })
}

const resetAllPayments = () => {
  Object.values(paymentsMap.value).forEach((paymentInfo) => {
    paymentInfo[isSelectedSymbol] = null
  })
}

const queryClient = useQueryClient()
const hasUpdateStatusQuery = ref(
  route.query.basketId !== undefined && route.query.instructionId !== undefined
)
if (hasUpdateStatusQuery.value) {
  ;(async () => {
    const res = await pejer.fetch(pejer.urls.bankIntegration.updateSignPaymentsStatus, {
      pathArgs: {
        BankIntegrationConsentID: consentID,
        BankIntegrationBasketID: basketID,
      },
      body: {
        InstructionID: route.query.instructionId,
        Status: route.query.scaStatus as 'success' | string,
      },
      ignoreAuthorization: true,
    })
    queryClient.invalidateQueries({
      queryKey: ['bank-integration'],
    })
    if ('error' in res === false || res.statusCode === 409) {
      // we remove the query params to avoid accidentally re-triggering the update if the user refreshes the page
      const url = new URL(window.location.href)
      url.searchParams.delete('basketId')
      url.searchParams.delete('instructionId')
      url.searchParams.delete('scaStatus')
      window.history.replaceState({}, document.title, url.toString())
    }
    hasUpdateStatusQuery.value = false
  })()
}
</script>
<template>
  <div class="flex flex-col gap-4 w-full h-full p-0 items-center">
    <div class="w-full items-center flex flex-col">
      <nav class="py-4 flex justify-between items-end max-w-screen-lg w-full">
        <h1 class="font-bold text-xl text-gray-700 flex items-center gap-2">
          Bankintegrasjon <InfoIcon :size="16" />
        </h1>
        <div
          class="flex py-2 px-3 rounded-md bg-white text-sm text-gray-500 items-center gap-2 font-semibold border-slate-200 border"
        >
          <template v-if="consent?.status === 'Active'">
            <span class="w-2 h-2 rounded-full bg-green-500"></span>
            Integrasjon aktiv
          </template>
          <template v-else-if="consent?.status === 'Revoked'">
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
            Samtykke trekt
          </template>
          <template v-else-if="consent?.status === 'OnboardingFailed'">
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
            Onboarding mislyktes
          </template>
          <template
            v-else-if="consent?.status === 'Initiated' || consent?.status === 'AwaitingOnboarding'"
          >
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på onboarding
          </template>
          <template v-else-if="consent?.status === 'AwaitingSigning'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på signering
          </template>
          <template v-else-if="consent?.status === 'AwaitingConsent'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på samtykke
          </template>
          <template v-else-if="consent?.status === 'AwaitingOnboardingApproval'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på godkjenning av onboarding
          </template>
          <template v-else>
            <span class="w-2 h-2 rounded-full bg-gray-500"></span>
            Status ukjent
          </template>
        </div>
      </nav>
      <Separator />
    </div>
    <DataStatePage v-bind="consentDataState">
      <DataStatePage v-bind="basketDataState">
        <DataStatePage :is-loading="ref(hasUpdateStatusQuery)" :is-success="ref(true)">
          <section class="max-w-screen-lg w-full flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-lg font-semibold">Bankbetalinger</h2>
                <p class="text-sm text-gray-500 max-w-[70ch]">
                  Bankene har begrenset hvor mange betalinger som kan signeres samtidig, betalingene
                  er derfor gruppert rundt dette slik at det kreves så få signaturer som mulig.
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 relative">
              <div class="flex flex-col gap-2 col-span-2 relative">
                <!-- <div class="flex gap-2 items-center sticky top-0 bg-white/0 backdrop-blur-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    class="bg-white w-fit"
                    @click="selectAllPayments"
                  >
                    <CheckSquare2 class="mr-1 h-4" />
                    Velg alle betalinger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="bg-white w-fit"
                    @click="resetAllPayments"
                  >
                    <SquareSlash class="mr-1 h-4" />
                    Nullstill valg
                  </Button>

                  <div
                    v-if="currentlyChosenBank"
                    class="text-xs text-gray-500 ml-auto flex flex-col h-full justify-end"
                  >
                    <Badge class="font-normal border-primary text-primary" variant="outline">
                      Valgt bank: &nbsp;
                      <strong>{{ currentlyChosenBank?.bic ?? 'Ingen bank valgt' }}</strong>
                    </Badge>
                  </div>
                </div> -->
                <Separator class="my-0" />
                <div
                  v-if="paymentGroups.length !== 0"
                  v-for="(payment, idx) in paymentGroups"
                  :key="idx"
                  class="flex gap-4 w-full bg-white p-0 border border-slate-200 rounded-md items-center"
                >
                  <div
                    class="font-normal text-base bg-slate-50 h-full p-4 border-r-slate-200 border-r rounded-l-sm"
                    variant="outline"
                  >
                    {{ idx + 1 }}
                  </div>
                  <h1 class="font-semibold text-md flex gap-2 items-center text-primary">
                    {{ banks?.[payment.bic]?.name ?? payment.bic }}
                  </h1>
                  <Separator orientation="vertical" class="h-6" />

                  <div class="flex gap-2 items-center text-sm text-gray-500">
                    <span>
                      {{ payment.payments.length }}
                      {{ payment.payments.length === 1 ? 'betaling' : 'betalinger' }}
                    </span>
                    •
                    <span
                      >{{
                        (
                          payment.payments.reduce(
                            (acc, payment) => acc + payment.amount / 100,
                            0
                          ) ?? 0
                        ).toLocaleString('nb-NO', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })
                      }}
                      {{ payment.payments?.[0]?.currency }}
                    </span>
                  </div>

                  <Button
                    class="ml-auto mr-4"
                    variant="default"
                    @click="
                      () => {
                        signPaymentsMutation({
                          pathArgs: {
                            BankIntegrationConsentID: consentID,
                            BankIntegrationBasketID: basketID,
                          },
                          body: {
                            BankIntegrationPaymentIDs: payment.payments.map(
                              (payment) => payment.bankIntegrationPaymentID
                            ),
                          },
                        })
                      }
                    "
                  >
                    <Signature class="h-4 w-4" /> Gå til signering
                  </Button>
                </div>
                <!-- <div
                  v-if="pendingPayments.length !== 0"
                  v-for="(payment, idx) in pendingPayments"
                  :key="payment.bankIntegrationPaymentID"
                  class="flex gap-4 w-full bg-white p-4 border border-slate-200 rounded-md items-center"
                >
                
                  <GetValueHelper
                    :value="{
                      paymentInfo: paymentsMap[payment.bankIntegrationPaymentID],
                      matchesChosenBank:
                        currentlyChosenBank === null ||
                        getPaymentBank(payment)?.bic === currentlyChosenBank.bic,
                    }"
                    v-slot="{ value: { paymentInfo, matchesChosenBank } }"
                  >
                    <div
                      class="aspect-square flex flex-col items-center justify-center shrink-0 border-r border-slate-200 cursor-pointer w-12"
                      @click="
                        () => {
                          if (!matchesChosenBank) return
                          paymentInfo[isSelectedSymbol] = paymentInfo[isSelectedSymbol]
                            ? null
                            : new Date()
                        }
                      "
                    >
                      <Checkbox
                        :checked="paymentInfo[isSelectedSymbol] !== null"
                        class="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500 rounded-md border-slate-300 pointer-events-none"
                        :disabled="!matchesChosenBank"
                      />
                    </div>
                    <div
                      class="aspect-square rounded-md bg-gray-50 flex items-center justify-center shrink-0 w-12"
                    >
                      <HandCoins class="text-gray-400 h-full" />
                    </div>
                    <div class="flex flex-col gap-1 w-full">
                      <div class="flex flex-col sm:flex-row gap-2 w-full">
                        <div class="rounded-md flex flex-col gap-0">
                          <h3 class="font-semibold text-md">
                            {{ payment.description ?? 'Ingen beskrivelse' }}
                          </h3>
                          <span class="text-xs text-muted-foreground">
                            <span class="font-semibold">
                              Til kontonummer: {{ payment.creditorAccountBban }}</span
                            ><br />
                          </span>
                        </div>
                        <div class="flex flex-col sm:items-end sm:ml-auto">
                          <span class="text-md text-green-500 font-semibold">
                            {{
                              (payment.amount / 100).toLocaleString('nb-NO', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })
                            }}
                            {{ payment.currency }}
                          </span>
                          <span class="font-semibold text-xs text-muted-foreground"
                            >Overføringsdato: {{ payment.executionDate }}</span
                          >
                        </div>
                      </div>
                      <span v-if="!matchesChosenBank" class="text-xs text-yellow-500">
                        Denne betalingen bruker en annen bank enn de som er valgt, vennligst fullfør
                        den bankens betalinger først.
                      </span>
                    </div>
                  </GetValueHelper> 
                </div>-->
                <div
                  class="w-full flex flex-col gap-2 items-center justify-center h-full py-8"
                  v-else
                >
                  <h2 class="text-lg font-semibold">Du er ferdig med alle betalinger</h2>
                  <p>Du har fullført alle betalingene.</p>
                  <p v-if="!basket?.data.redirectBackUrl" class="text-muted-foreground">
                    Du kan lukke vinduet.
                  </p>
                  <a v-else :href="basket?.data.redirectBackUrl" class="text-primary font-semibold">
                    <Button class="self-end" variant="default"> Gå videre </Button>
                  </a>
                </div>
              </div>
              <div class="flex flex-col gap-2 sticky top-10 self-start">
                <!-- <div class="w-full border rounded-md border-slate-200 bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead :colspan="2" class="p-1">
                          <DataStateButton
                            :data-state="signPaymentsDataState"
                            class="w-full"
                            :disabled="chosenPayments.length === 0"
                            @click="signPayments"
                          >
                            <template v-if="chosenPayments.length === 0"
                              >Vennligst velg betalinger</template
                            >
                            <template v-else>
                              Gå til {{ currentlyChosenBank?.name ?? 'banken' }} for å
                              godkjenne&nbsp;
                              <strong>{{ chosenPayments.length }}</strong> &nbsp;betalinger
                            </template>
                          </DataStateButton>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell class="p-3">Antall valgte betalinger</TableCell>
                        <TableCell class="p-3 font-semibold">{{ chosenPayments.length }}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell class="p-3">Sum valgte betalinger</TableCell>
                        <TableCell class="p-3 font-semibold">
                          {{
                            (
                              chosenPayments.reduce(
                                (acc, payment) => acc + payment.amount / 100,
                                0
                              ) ?? 0
                            ).toLocaleString('nb-NO', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })
                          }}
                          {{ pendingPayments?.[0]?.currency }}</TableCell
                        >
                      </TableRow>
                      <TableRow>
                        <TableCell class="p-3">Bank</TableCell>
                        <TableCell class="p-3 font-semibold">
                          {{ currentlyChosenBank?.name ?? '-' }}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell class="p-3"> Antall betalinger per godkjennelse </TableCell>
                        <TableCell class="p-3 font-semibold">
                          {{ currentlyChosenBank?.basketSize ?? '-' }}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div> 
                <Separator class="mt-2" />-->
                <!-- <h3 class="text-md font-semibold flex items-center">Alle betalinger</h3> -->
                <div class="w-full border rounded-md border-slate-200 bg-white">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell class="p-3">Antall påkrevde signeringer</TableCell>
                        <TableCell class="p-3 font-semibold">
                          {{ paymentGroups.length }}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell class="p-3">Antall betalinger</TableCell>
                        <TableCell class="p-3 font-semibold">{{
                          pendingPayments.length
                        }}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell class="p-3">Sum betalinger</TableCell>
                        <TableCell class="p-3 font-semibold">
                          {{
                            (
                              pendingPayments.reduce(
                                (acc, payment) => acc + payment.amount / 100,
                                0
                              ) ?? 0
                            ).toLocaleString('nb-NO', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })
                          }}
                          {{ pendingPayments?.[0]?.currency }}</TableCell
                        >
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </DataStatePage>
      </DataStatePage>
    </DataStatePage>
  </div>
</template>
